/**
 * ============================================================================
 *  File        : frontend/src/data/ef260128Sample.ts
 *  Module      : data
 *  Purpose     : EF260128 黃金案件 16 品項範例（開發用，填表加速 UI 驗收）
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  資料來源：業主基準 Excel ① sheet。對齊 v13 unified create item DTO：
 *  declared_weight_kg = ①E 重量(フ入り)、min_unit_price = ①K 最低単価(税別)。
 *  荷姿 ①I：フレコン→flexible、バラ→bara。uom 一律 kg。
 *  SUM(E×K)=30,355,365 → ×1.1 = L34 33,390,901.5（P1 黃金驗收值）。
 */

/** 後端 uom（単位）允許值。 */
export type Uom = 'kg' | 'pcs'
/** 後端 packaging（梱包形態）允許值（對齊精算 package_type）。 */
export type Packaging = 'flexible' | 'small_flexible' | 'bara' | 'pallet' | 'other'

/** 新增案件表單的單列品項（v13 create item 語意）。 */
export interface CaseItemFormRow {
  itemName: string
  declaredWeightKg: string
  minUnitPrice: string
  quantity: string
  uom: Uom
  packaging: Packaging
  bagUnitWeightKg: string
}

/** 空白品項列（新增表單預設一列用）。 */
export function emptyItemRow(): CaseItemFormRow {
  return {
    itemName: '',
    declaredWeightKg: '',
    minUnitPrice: '',
    quantity: '1',
    uom: 'kg',
    packaging: 'flexible',
    bagUnitWeightKg: '',
  }
}

/** フレコン（flexible）品項列。 */
function bagRow(
  itemName: string,
  declaredWeightKg: string,
  minUnitPrice: string,
  bags: string,
): CaseItemFormRow {
  return {
    itemName,
    declaredWeightKg,
    minUnitPrice,
    quantity: bags,
    uom: 'kg',
    packaging: 'flexible',
    bagUnitWeightKg: '',
  }
}

/** バラ（bara）品項列（無袋重）。 */
function looseRow(
  itemName: string,
  declaredWeightKg: string,
  minUnitPrice: string,
): CaseItemFormRow {
  return {
    itemName,
    declaredWeightKg,
    minUnitPrice,
    quantity: '1',
    uom: 'kg',
    packaging: 'bara',
    bagUnitWeightKg: '',
  }
}

/**
 * EF260128 ① sheet 的 16 筆有效品項（faithful；業主基準 Excel 原值）。
 * declared_weight_kg = ①E 重量(フ入り)、min_unit_price = ①K 最低単価(税別)。
 * SUM(E×K) = 30,355,365 → ×1.1 = L34 33,390,901.5（P1 黃金驗收值）。
 */
export const EF260128_SAMPLE_ITEMS: CaseItemFormRow[] = [
  bagRow('汚れ線', '1294', '2000', '1'),
  bagRow('並銅', '622', '1940', '1'),
  bagRow('込銅', '2422', '1920', '3'),
  bagRow('メッキ銅', '622', '1920', '2'),
  bagRow('異物付エナメル銅線', '2637', '1865', '2'),
  bagRow('銅釜（白）', '540', '1780', '1'),
  bagRow('銅フィンステン付', '576', '1650', '2'),
  bagRow('込砲金', '1314', '1780', '2'),
  looseRow('込砲金オーバーサイズ', '372', '1750'),
  bagRow('高力黄銅', '461', '1300', '1'),
  looseRow('高力黄銅オーバーサイズ', '57', '1250'),
  bagRow('込真鍮', '2388', '1160', '2'),
  bagRow('VA線（太）', '3705', '850', '4'),
  bagRow('VA線', '2988', '820', '3'),
  bagRow('OA線', '1740', '720', '3'),
  bagRow('家電雑線', '888', '680', '1'),
]

export const EF260128_TITLE = 'EF260128 黃金案件範例（16 品項）'
